import type { MessageId } from "@redotech/effect-api-shared/ids/definitions";
import type { EmailMessage } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/email";
import { sortMessagesByReplyChain } from "@redotech/util/topological-sort";
import { useCallback, useMemo, useRef, useState } from "react";
import type {
  EmailContact,
  FormattedEmailMessage,
} from "../components/message-types/email-message-card";
import type { DraftCollectionItem } from "../db/collections";
import type { ReplyMode } from "./use-reply-mode";

export interface EmailThreadViewState {
  /** Formatted messages ready for display */
  formattedMessages: FormattedEmailMessage[];
  /** Check if a specific message card is expanded */
  isMessageExpanded: (messageId: MessageId) => boolean;
  /** Toggle expansion state of a message card */
  toggleMessageExpanded: (messageId: MessageId) => void;
  /** Expand all message cards */
  expandAll: () => void;
  /** Collapse all message cards (keeps last message expanded) */
  collapseAll: () => void;
  /** Check if the reply input is open for a message */
  isReplyOpen: (messageId: MessageId) => boolean;
  /** Open the reply input for a message in a specific mode */
  openReply: (messageId: MessageId, mode: ReplyMode) => void;
  /** Close the reply input for a message */
  closeReply: (messageId: MessageId) => void;
  /** Get the reply mode for a message */
  getReplyMode: (messageId: MessageId) => ReplyMode;
}

/**
 * Hook to manage email thread display state.
 * Combines message formatting, card expansion, and reply-open state.
 * Messages with drafts are auto-expanded and have reply inputs open.
 */
export function useEmailThreadView(
  messages: EmailMessage[],
  drafts: DraftCollectionItem[],
): EmailThreadViewState {
  // Sort messages chronologically with stable topological ordering
  const sortedMessages = useMemo(() => {
    return sortMessagesByReplyChain(messages);
  }, [messages]);

  const lastMessageId = useMemo(() => {
    if (sortedMessages.length === 0) {
      return null;
    }
    return sortedMessages[sortedMessages.length - 1]?.id ?? null;
  }, [sortedMessages]);

  // Build set of message IDs that have drafts (shared by expansion + reply state)
  const messageIdsWithDrafts = useMemo(() => {
    const ids = new Set<MessageId>();
    for (const draft of drafts) {
      const replyToId =
        (draft.inReplyToMessageId as MessageId | undefined) ?? null;
      if (replyToId) {
        ids.add(replyToId);
      }
    }
    return ids;
  }, [drafts]);

  // --- Card expansion state ---

  const initiallyExpandedIds = useMemo(() => {
    const ids = new Set<MessageId>(messageIdsWithDrafts);
    if (lastMessageId) {
      ids.add(lastMessageId);
    }
    return ids;
  }, [messageIdsWithDrafts, lastMessageId]);

  const [expandedMessageIds, setExpandedMessageIds] = useState<Set<MessageId>>(
    () => new Set(initiallyExpandedIds),
  );

  // When new default IDs arrive (e.g. drafts loaded async), expand them
  const seenExpandedIds = useRef<Set<string>>(initiallyExpandedIds);
  if (initiallyExpandedIds !== seenExpandedIds.current) {
    const prev = seenExpandedIds.current;
    seenExpandedIds.current = initiallyExpandedIds;
    setExpandedMessageIds((state) => {
      const next = new Set(state);
      let changed = false;
      for (const id of initiallyExpandedIds) {
        if (!prev.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : state;
    });
  }

  const toggleMessageExpanded = useCallback((messageId: MessageId) => {
    setExpandedMessageIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  }, []);

  const isMessageExpanded = useCallback(
    (messageId: MessageId) => {
      return expandedMessageIds.has(messageId);
    },
    [expandedMessageIds],
  );

  const expandAll = useCallback(() => {
    setExpandedMessageIds(new Set(messages.map((m) => m.id)));
  }, [messages]);

  const collapseAll = useCallback(() => {
    const newSet = new Set<MessageId>();
    if (lastMessageId) {
      newSet.add(lastMessageId);
    }
    setExpandedMessageIds(newSet);
  }, [lastMessageId]);

  // --- Reply-open state ---

  const initialReplyOpenIds = useMemo(() => {
    const ids = new Set<MessageId>();
    for (const id of messageIdsWithDrafts) {
      ids.add(id);
    }
    if (ids.size === 0) {
      const customerMessages = sortedMessages.filter(
        (m) => m.origin === "external",
      );
      if (customerMessages.length > 0) {
        const lastCustomerMessage =
          customerMessages[customerMessages.length - 1];
        if (lastCustomerMessage) {
          ids.add(lastCustomerMessage.id);
        }
      }
    }
    return ids;
  }, [messageIdsWithDrafts, sortedMessages]);

  const [replyOpenIds, setReplyOpenIds] = useState<Set<MessageId>>(
    () => new Set(initialReplyOpenIds),
  );

  // Track reply mode per message (defaults to "reply")
  const [replyModeMap, setReplyModeMap] = useState<Map<MessageId, ReplyMode>>(
    () => new Map(),
  );

  const seenReplyIds = useRef<Set<MessageId>>(initialReplyOpenIds);
  if (initialReplyOpenIds !== seenReplyIds.current) {
    const prev = seenReplyIds.current;
    seenReplyIds.current = initialReplyOpenIds;
    setReplyOpenIds((state) => {
      const next = new Set(state);
      let changed = false;
      for (const id of initialReplyOpenIds) {
        if (!prev.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : state;
    });
  }

  const isReplyOpen = useCallback(
    (messageId: MessageId): boolean => replyOpenIds.has(messageId),
    [replyOpenIds],
  );

  const openReply = useCallback((messageId: MessageId, mode: ReplyMode) => {
    setReplyModeMap((prev) => {
      const next = new Map(prev);
      next.set(messageId, mode);
      return next;
    });
    setReplyOpenIds((prev) => {
      if (prev.has(messageId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(messageId);
      return next;
    });
  }, []);

  const closeReply = useCallback((messageId: MessageId) => {
    setReplyOpenIds((prev) => {
      if (!prev.has(messageId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(messageId);
      return next;
    });
  }, []);

  const getReplyMode = useCallback(
    (messageId: MessageId): ReplyMode => replyModeMap.get(messageId) ?? "reply",
    [replyModeMap],
  );

  // Format messages for display
  const formattedMessages = useMemo(() => {
    return sortedMessages.map((message) => formatEmailMessage(message));
  }, [sortedMessages]);

  return {
    formattedMessages,
    isMessageExpanded,
    toggleMessageExpanded,
    expandAll,
    collapseAll,
    isReplyOpen,
    openReply,
    closeReply,
    getReplyMode,
  };
}

/**
 * Pure function to format a single email message for display.
 * Uses current branch field names: addressType, email, name
 */
function formatEmailMessage(message: EmailMessage): FormattedEmailMessage {
  const isMessageFromCustomer = message.origin === "external";
  const addresses = message.channelData?.addresses ?? null;

  // Extract sender from "from" address
  const fromAddress = addresses?.find((a) => a.addressType === "from");
  const senderName =
    fromAddress?.name ??
    fromAddress?.email ??
    (isMessageFromCustomer ? "Customer" : "Agent");
  const senderInitials = senderName.charAt(0).toUpperCase();

  // Extract all address types
  const toAddresses = addresses?.filter((a) => a.addressType === "to") ?? [];
  const ccAddresses = addresses?.filter((a) => a.addressType === "cc") ?? [];
  const bccAddresses = addresses?.filter((a) => a.addressType === "bcc") ?? [];

  // Structured address fields - from is always required
  const from: EmailContact = fromAddress
    ? toEmailContact(fromAddress)
    : { name: senderName, email: "" };
  const to = toAddresses.map(toEmailContact);
  const cc = ccAddresses.map(toEmailContact);
  const bcc = bccAddresses.map(toEmailContact);

  // Generate summary for collapsed view
  const allRecipients = [...toAddresses, ...ccAddresses, ...bccAddresses];
  const recipientSummary = formatRecipientSummary(allRecipients);

  const content = message.content ?? "";
  const htmlContent = message.channelData?.parsedHtmlBody ?? null;
  const preview = getMessagePreview(content);

  const formattedTime = formatMessageTime(message.sentAt);

  return {
    id: message.id,
    messageId: message.id,
    threadId: message.conversationThreadId,
    senderName,
    senderInitials,
    isMessageFromCustomer,
    from,
    to,
    cc,
    bcc,
    recipientSummary,
    content,
    htmlContent,
    timestamp: message.sentAt,
    formattedTime,
    preview,
  };
}

function getMessagePreview(content: string, maxLength = 100): string {
  if (!content) {
    return "(No content)";
  }
  const stripped = content.replace(/\s+/g, " ").trim();
  if (stripped.length <= maxLength) {
    return stripped;
  }
  return stripped.substring(0, maxLength - 3) + "...";
}

function formatMessageTime(date: Date): string {
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  const isThisYear = date.getFullYear() === now.getFullYear();
  if (isThisYear) {
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatRecipientSummary(
  recipients: readonly { name: string | null; email: string }[],
): string | null {
  if (recipients.length === 0) {
    return null;
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const first = recipients[0].name ?? recipients[0].email;
  if (recipients.length === 1) {
    return first;
  }
  return `${first}, +${recipients.length - 1} more`;
}

function toEmailContact(a: {
  name: string | null;
  email: string;
}): EmailContact {
  return { name: a.name, email: a.email };
}
